import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useTranslation, Trans } from 'react-i18next'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { InclusionsV2Section } from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import { InclusionsSection } from 'components/OfferPage/FlashHotel/InclusionsSection'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'

const BorderedContainer = styled.div`
  border-left: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-right: 1px solid ${({ theme }) => theme.palette.neutral.five};
  padding: ${rem(16)};
  min-width: ${rem(320)};
`

interface MappedProps {
  checkIn?: moment.Moment;
  duration?: number;
}

interface Props {
  pkg: App.Package;
}

const imageProps: ImageParams = {
  xs: '5:3',
  md: '2:1',
  quality: 'good',
}

function LePackageUpgradeColumnScrollable({
  checkIn,
  duration,
  pkg,
}: Props & MappedProps) {
  const { t } = useTranslation(['common', 'offerPage'])

  const { roomType } = pkg
  const firstRoomImageId = roomType?.images?.[0]?.id

  const inclusionsV1 = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusionsV1 = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0
  const showInclusionV1 = hasInclusionsV1 && (inclusionsV1 || bonusInclusionsV1)

  return (
    <BorderedContainer>
      <Group direction="vertical" gap={12}>

        {firstRoomImageId && (
          <ResponsiveImage
            {...imageProps}
            id={firstRoomImageId}
          />
        )}

        {roomType?.sizeSqm && <BodyText variant="small">
          <Trans i18nKey="roomSizeWithUnit" ns="offerPage">
            <b>Room size:</b> {{ roomSize: roomType?.sizeSqm }}m<sup>2</sup>
          </Trans>
        </BodyText>}

        {showInclusionV1 && (
          <Group direction="vertical" gap={12}>
            {(inclusionsV1 && !bonusInclusionsV1) && <BodyText variant="small" weight="bold">{t('Inclusions', { ns: 'common' })}:</BodyText>}
            <InclusionsSection inclusions={inclusionsV1} bonusInclusions={bonusInclusionsV1} size="small" />
          </Group>
        )}
        {!hasInclusionsV1 && pkg.inclusionsV2 && (
          <Group direction="vertical" gap={12}>
            <BodyText variant="small" weight="bold">{t('inclusionCount', { ns: 'common', count: pkg.inclusionsV2.length })}:</BodyText>
            <InclusionsV2Section inclusions={pkg.inclusionsV2} size="small" />
          </Group>
        )}

        <LePackageUpgradeCardRoomDetails roomType={roomType} />
      </Group>
    </BorderedContainer>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState): MappedProps => ({
  checkIn: state.checkInDate,
  duration: state.duration,
}))(LePackageUpgradeColumnScrollable)
